import React from 'react';

export default function RecommendationCard({ title, data }) {
    if (!data) return null;

    // Handle data mapping bugs: assessmentRecommendations sends 'urgency', but old code expected 'tingkatUrgency'
    const urgency = data.urgency || data.tingkatUrgency || 'low';
    // Handle followUp bug: assessmentRecommendations sends 'followUp', old code expected 'tindakLanjut'
    const tindakLanjut = data.followUp || data.tindakLanjut;

    const isCritical = urgency === 'high';

    const getStatusColor = (status) => {
        if (!status) return 'text-gray-600 bg-gray-50 border-gray-200';
        
        if (status.includes('Sesuai') || status.includes('Normal') || status.includes('Rendah')) {
            return 'text-emerald-700 bg-emerald-50 border-emerald-200';
        } else if (status.includes('Meragukan') || status.includes('Sedang') || status.includes('Ringan')) {
            return 'text-amber-700 bg-amber-50 border-amber-200';
        } else if (status.includes('Penyimpangan') || status.includes('Tinggi') || status.includes('GPPH') || status.includes('Gangguan') || status.includes('Terdeteksi')) {
            return 'text-rose-700 bg-rose-50 border-rose-200 shadow-sm shadow-rose-100';
        }
        return 'text-gray-700 bg-gray-50 border-gray-200';
    };

    if (isCritical) {
        return (
            <div className="mb-5 relative group overflow-hidden rounded-[1.5rem] p-5 bg-gradient-to-br from-rose-500 to-red-600 border border-red-400 shadow-[0_10px_40px_rgba(225,29,72,0.3)]">
                {/* Decorative blob for critical card */}
                <div className="absolute -top-10 -right-10 w-32 h-32 bg-white rounded-full mix-blend-overlay filter blur-xl opacity-20 pointer-events-none"></div>
                
                <div className="relative z-10">
                    <div className="flex justify-between items-start mb-4">
                        <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center text-xl shadow-inner border border-white/30">
                                🚨
                            </div>
                            <div>
                                <h3 className="text-lg font-extrabold text-white tracking-tight leading-tight">{title}</h3>
                                <span className="inline-block px-2.5 py-0.5 mt-1 rounded-full text-[10px] font-bold tracking-widest uppercase bg-white text-rose-600 shadow-sm">
                                    Tindakan Segera
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white/10 backdrop-blur-sm border border-white/20 p-4 rounded-xl mb-4">
                        <div className="font-extrabold text-white text-lg mb-1">{data.status}</div>
                        {data.detail && <div className="text-sm text-red-50 mt-1">{data.detail}</div>}
                        <p className="text-red-50 text-sm mt-3 border-t border-white/20 pt-3 leading-relaxed font-medium">
                            {data.interpretasi}
                        </p>
                    </div>

                    {tindakLanjut && (
                        <div className="bg-white p-4 rounded-xl mb-3 shadow-inner">
                            <h4 className="font-extrabold text-rose-600 mb-1 flex items-center text-sm uppercase tracking-wider">
                                <span className="mr-2">⚡</span> Instruksi Rujukan
                            </h4>
                            <p className="text-gray-800 text-sm font-bold leading-relaxed">{tindakLanjut}</p>
                        </div>
                    )}

                    {data.rekomendasi && (
                        <div className="bg-rose-900/40 border border-rose-800/50 p-4 rounded-xl backdrop-blur-md">
                            <h4 className="font-bold text-rose-100 mb-2 text-xs uppercase tracking-widest">Saran Tambahan</h4>
                            <ul className="text-white text-sm space-y-1.5 list-disc list-inside opacity-90">
                                {Array.isArray(data.rekomendasi) ? (
                                    data.rekomendasi.map((item, index) => <li key={index}>{item}</li>)
                                ) : (
                                    <li className="list-none">{data.rekomendasi}</li>
                                )}
                            </ul>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Normal Card (Low / Medium Urgency)
    return (
        <div className="mb-5 bg-white/70 backdrop-blur-xl border border-white/80 rounded-[1.5rem] p-5 shadow-[0_8px_30px_rgba(0,0,0,0.04)] transition-all hover:shadow-[0_8px_30px_rgba(0,0,0,0.08)]">
            <div className="space-y-4">
                {/* Header */}
                <div className="flex justify-between items-start">
                    <h3 className="text-lg font-extrabold text-gray-800 tracking-tight">{title}</h3>
                    {urgency === 'medium' && (
                        <span className="px-3 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase border bg-amber-100 border-amber-300 text-amber-800">
                            Perhatian
                        </span>
                    )}
                </div>

                {/* Status */}
                <div className={`p-4 rounded-xl border ${getStatusColor(data.status)}`}>
                    <div className="font-extrabold">{data.status}</div>
                    {data.detail && <div className="text-sm mt-1 opacity-90">{data.detail}</div>}
                    <p className="text-sm mt-2 opacity-90 leading-relaxed font-medium">
                        {data.interpretasi}
                    </p>
                </div>

                {/* Tindak Lanjut */}
                {tindakLanjut && (
                    <div className="bg-sky-50 border border-sky-100 p-4 rounded-xl">
                        <h4 className="font-bold text-sky-800 mb-1 text-xs uppercase tracking-widest">Tindak Lanjut</h4>
                        <p className="text-sky-900 text-sm font-semibold">{tindakLanjut}</p>
                    </div>
                )}

                {/* Rekomendasi */}
                {data.rekomendasi && (
                    <div className="bg-gray-50 border border-gray-100 p-4 rounded-xl">
                        <h4 className="font-bold text-gray-600 mb-2 text-xs uppercase tracking-widest">Rekomendasi</h4>
                        <ul className="text-gray-700 text-sm space-y-1.5 list-disc list-inside">
                            {Array.isArray(data.rekomendasi) ? (
                                data.rekomendasi.map((item, index) => <li key={index}>{item}</li>)
                            ) : (
                                <li className="list-none">{data.rekomendasi}</li>
                            )}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
}
